import {
  FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS,
  FEATURE_ENABLE_INVOICING_NON_FULFILLMENT_PAYMENT_REQUESTS,
} from '../../../../../constants'
import { isValidCustomer } from '../../../../../functions/sale'
import { useRegisterManager } from '../../../../../ng-compat/hooks'
import { useAppSelector } from '../../../../../state/hooks'
import {
  selectSaleCustomer,
  selectSaleHasLineItemFulfilment,
} from '../../../../../state/slices/currentSale/currentSaleSlice'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { isAUComplianceFeatureEnabled } from '../../../../../functions/invoicing/payment-request-resources'
import { useFetchPaymentTypes } from '../../hooks/useFetchPaymentTypes'
import { useIsLayawayEligibleForPaymentRequest } from './useIsLayawayEligibleForPaymentRequest'

export interface ShowPaymentRequestButtonParams {
  amountTendered: string
  hasPaymentAmountError: boolean
}

export interface ShowPaymentRequestButtonResult {
  showButton: boolean
  canLayaway: boolean
}

/**
 * Hook to determine if the Payment Request button should be visible
 * Shows when either:
 * 1. Has fulfillment line items OR
 * 2. Layaway is available as a payment option for the sale (non-fulfillment)
 * Note that button should be enabled for partial payments on non-fulfillment sale (even if Layaway button is disabled),
 * in which case the payment request is created with the partial amount and the sale is layaway with full outstanding amount
 *
 * Both scenarios require: outlet in a supported country (US, CA; or AU when AU compliance FF is enabled), valid customer, and LSPay payment type
 */
export const useShowPaymentRequestButton = ({
  amountTendered,
  hasPaymentAmountError,
}: ShowPaymentRequestButtonParams): ShowPaymentRequestButtonResult => {
  const hasFulfillmentLineItems = useAppSelector(
    selectSaleHasLineItemFulfilment
  )
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()

  const { systemPaymentTypes } = useFetchPaymentTypes()
  const hasLSPay = !!systemPaymentTypes.lspayPaymentType

  const isLayawayEligible = useIsLayawayEligibleForPaymentRequest(
    hasPaymentAmountError
  )

  const isPaymentRequestFeatureEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_PAYMENT_REQUESTS
  )

  if (!isPaymentRequestFeatureEnabled) {
    return {
      showButton: false,
      canLayaway: false,
    }
  }

  const isNonFulfillmentPaymentRequestsFeatureEnabled = getFeatureFromCache(
    FEATURE_ENABLE_INVOICING_NON_FULFILLMENT_PAYMENT_REQUESTS
  )

  const canLayaway =
    isNonFulfillmentPaymentRequestsFeatureEnabled && isLayawayEligible

  const countryId = currentOutlet?.physical_country_id
  const isOutletInSupportedCountry =
    countryId === 'US' ||
    countryId === 'CA' ||
    (countryId === 'AU' && isAUComplianceFeatureEnabled())

  // Common requirements for all payment request scenarios
  const hasCommonRequirements =
    isOutletInSupportedCountry && isValidCustomer(saleCustomer) && hasLSPay

  // Show the button when hasCommonRequirements AND either:
  //  - Has fulfillment line items OR
  //  - Layaway is eligible (including partial payments for non-fulfillment)
  const showButton =
    hasCommonRequirements && (hasFulfillmentLineItems || canLayaway)

  return {
    showButton,
    canLayaway: hasCommonRequirements && canLayaway,
  }
}
